import { useEffect, useRef, useState } from "react";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { submitOnboarding } from "../../../../services/onboardingService";
import axios from "../../../../services/axios";
import { useNavigate } from "react-router-dom";
import Constants from "../../../../constants/Index";
import { AlertModal } from "../../../../components/Modal/Alert/Index";
import { SimpleButtonClose } from "../../../../components/Modal/Contents/SimpleButtonClose/Index";
import { TitleModal } from "../../../../components/Modal/Contents/TitleModal/Index";
import { ButtonRedirect } from "../../../../components/Modal/Error/ButtonRedirect/Index";
import { calculateHectares } from "../../../../utils/geolib/calculateHectares";
import { FarmMapCard } from "./FarmMapCard";
import { Loading } from "../../../../components/Loading/Index";
import "./styles.css";
import { useTranslation } from "react-i18next";

type SummaryProps = {
  reviewOnly?: boolean;
};

type ApiFieldError = {
  field: string;
  error: string;
};

function formatPhone(phone: string) {
  const digits = phone.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function normalizeApiField(field: string) {
  return field.replace(/^microAreas\[/, "micro_areas[");
}

export function Summary({ reviewOnly = false }: SummaryProps) {
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement>(null);
  const { payload, setApiFieldErrors, markStepError } = useOnboarding();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const contentStyle = {
    width: '440px',
    height: '218px',
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '10px'
  } as React.CSSProperties;

  function getErrorTarget(field: string) {
    field = normalizeApiField(field);

    if (field === "farm_initials_key") return { section: 1, subSection: 3 };

    const match = field.match(/^(\w+)\[(\d+)\]/);
    if (!match) {
      if (field.startsWith("assortment")) return { section: 1, subSection: 2 };
      return null;
    }

    const [, collection, rawIndex] = match;
    const index = Number(rawIndex);

    if (collection === "users") return { section: 1, subSection: 0 };
    if (collection === "farms") return { section: 1, subSection: 3 };

    if (collection === "areas") {
      const area = payload?.areas?.[index];
      const farmIndex = (payload?.farms || []).findIndex((farm: any) => String(farm.uuid) === String(area?.farm_id));
      return farmIndex >= 0 ? { section: farmIndex + 2, subSection: 0 } : null;
    }

    if (collection === "micro_areas") {
      const microArea = payload?.micro_areas?.[index];
      const area = (payload?.areas || []).find((item: any) => String(item.uuid) === String(microArea?.area_id));
      const farmIndex = (payload?.farms || []).findIndex((farm: any) => String(farm.uuid) === String(area?.farm_id));
      return farmIndex >= 0 ? { section: farmIndex + 2, subSection: 1 } : null;
    }

    return null;
  }

  function applyApiErrors(errorsFields: ApiFieldError[] = []) {
    const nextErrors = errorsFields.reduce((errors, fieldError) => {
      const field = normalizeApiField(fieldError.field);

      if (field === "farm_initials_key") {
        errors["farms[0].initials"] = fieldError.error;
        return errors;
      }

      if (field && !errors[field]) errors[field] = fieldError.error;
      return errors;
    }, {} as Record<string, string>);

    setApiFieldErrors(nextErrors);
    errorsFields.forEach((fieldError) => {
      const target = getErrorTarget(fieldError.field);
      if (target) markStepError(target.section, target.subSection);
    });
  }

  async function handleSubmit() {
    if (reviewOnly) return false;
    if (!payload) return false;
    if (isSubmitting) return false;

    setIsSubmitting(true);
    try {
      setApiFieldErrors({});
      const processedPayload = await submitOnboarding(payload);

      await axios.post("/onboarding", processedPayload);
      localStorage.removeItem(Constants.LOCALSTORAGE_PAYLOAD);
      navigate(Constants.PADDOCKS_MAP);
      return true;
    } catch (error: any) {
      console.log(error.response);
      const errorsFields = error.response?.data?.errors_fields || [];
      applyApiErrors(errorsFields);

      if (errorsFields?.[0]?.field === "farm_initials_key") {
        setError(t("internalValidation.duplicateFarmInitials"));
      } else {
        setError(error.response?.data?.msg || t("internalValidation.finishOnboardingError"));
      }
      setAlertVisible(true);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <AlertModal visible={alertVisible} contentStyle={contentStyle}>
        <SimpleButtonClose onClick={() => setAlertVisible(false)} />
        <div className="createuser__alertModalBody">
          <TitleModal>{error}</TitleModal>
        </div>
        <ButtonRedirect onClick={() => setAlertVisible(false)}>{t("onboardingRemaining.back")}</ButtonRedirect>
      </AlertModal>
      <Loading visible={isSubmitting} />

      <main ref={mainRef as any}>
        <section className="summary__container">
          <div>
            <h2 className="summary__title">Perfis Criados ({payload.users.length})</h2>
            {/* Perfis */}
            {payload?.users && payload.users.length > 0 && (
              <div className="summary__section">
                <ul>
                  {payload.users.map((user, index) => (
                    <li key={index}>
                      <strong>{user.name}</strong>
                      {user.phone && <span> - {formatPhone(user.phone)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Mapas das Fazendas */}
            {payload?.farms && payload.farms.length > 0 && (
              <div style={{ marginTop: 30 }}>
                <h2 className="summary__title">Fazendas, Retiros e Pastos ({payload.farms.length})</h2>
                {payload.farms.map((farm, index) => (
                  <div key={farm.uuid || index}>
                    <FarmMapCard
                      farm={farm}
                      areas={payload.areas || []}
                      microAreas={payload.micro_areas || []}
                    />
                    {index < payload.farms.length - 1 && (
                      <div className="summary__farm-divider" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <footer>
        <OnboardingFooterV2
          nextStepEvent={handleSubmit}
          backStepEvent={async () => true}
          nextLabel={isSubmitting ? "Enviando..." : undefined}
          disabled={isSubmitting}
        />
      </footer>
    </>
  );
}
